import React, { useCallback, useEffect, useState } from "react";
import {
  Box, Typography, List, ListItemButton, TextField, Button, Stack, Chip, Divider, Alert,
  Dialog, DialogTitle, DialogContent, DialogActions, FormControlLabel, Checkbox,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { toUserSafeErrorMessage } from "../utils/errorMessage";
import {
  adminListPrompts, adminListVersions, adminGetVersion, adminCreateVersion, adminActivateVersion,
  PromptSummary, PromptVersionRow,
} from "../api/client";

export default function AdminPromptsPage() {
  const { t } = useTranslation("pages-core");
  const [prompts, setPrompts] = useState<PromptSummary[]>([]);
  const [sel, setSel] = useState<PromptSummary | null>(null);
  const [versions, setVersions] = useState<PromptVersionRow[]>([]);
  const [content, setContent] = useState("");
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  // Activation dialog state. Coach prompts are eval-gated: the backend 400s
  // unless an eval run id (or force) is supplied, so the dialog mirrors that.
  const [activateId, setActivateId] = useState<string | null>(null);
  const [evalRunId, setEvalRunId] = useState("");
  const [force, setForce] = useState(false);
  const isCoach = sel?.product === "coach";
  const activateDisabled = isCoach && !evalRunId.trim() && !force;

  const errMsg = useCallback(
    (e: unknown) => toUserSafeErrorMessage(e, t("AdminPromptsPage.requestFailed")),
    [t]
  );

  const refresh = async () => setPrompts(await adminListPrompts());
  useEffect(() => { refresh().catch((e) => setErr(errMsg(e))); }, [errMsg]);

  const select = async (p: PromptSummary) => {
    setSel(p); setMsg(null); setErr(null);
    try {
      setVersions(await adminListVersions(p.product, p.prompt_key, p.kind));
      const active = await adminGetVersion(p.version_id);
      setContent(active.content);
    } catch (e) {
      setErr(errMsg(e));
    }
  };

  const saveDraft = async () => {
    if (!sel) return;
    setErr(null);
    try {
      await adminCreateVersion({ product: sel.product, key: sel.prompt_key, kind: sel.kind, content });
      setMsg(t("AdminPromptsPage.draftSavedMessage"));
      setVersions(await adminListVersions(sel.product, sel.prompt_key, sel.kind));
    } catch (e) {
      setErr(errMsg(e));
    }
  };

  const openDeploy = (id: string) => {
    setEvalRunId("");
    setForce(false);
    setActivateId(id);
  };

  const confirmDeploy = async () => {
    if (!activateId) return;
    const id = activateId;
    setActivateId(null);
    setErr(null);
    try {
      await adminActivateVersion(id, {
        eval_run_id: evalRunId.trim() || undefined,
        force: force || undefined,
      });
      setMsg(t("AdminPromptsPage.deployedMessage"));
      // Reload the editor with the now-live content (so a rollback shows the
      // version we just activated, not the stale previously-loaded body).
      const live = await adminGetVersion(id);
      setContent(live.content);
      await refresh();
      if (sel) setVersions(await adminListVersions(sel.product, sel.prompt_key, sel.kind));
    } catch (e) {
      setErr(errMsg(e));
    }
  };

  return (
    <Box sx={{ display: "flex", gap: 3, p: 3 }}>
      <Box sx={{ width: 320 }}>
        <Typography variant="h6" gutterBottom>{t("AdminPromptsPage.promptsHeading")}</Typography>
        <List dense>
          {prompts.map((p) => (
            <ListItemButton key={p.version_id} selected={sel?.version_id === p.version_id} onClick={() => select(p)}>
              {p.product} / {p.prompt_key} / {p.kind} (v{p.active_version})
            </ListItemButton>
          ))}
        </List>
      </Box>
      <Divider orientation="vertical" flexItem />
      <Box sx={{ flex: 1 }}>
        {err && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErr(null)}>{err}</Alert>}
        {msg && <Alert severity="info" sx={{ mb: 2 }}>{msg}</Alert>}
        {sel ? (
          <Stack spacing={2}>
            <Typography variant="h6">{sel.product} / {sel.prompt_key} / {sel.kind}</Typography>
            <TextField multiline minRows={14} value={content} onChange={(e) => setContent(e.target.value)} fullWidth />
            <Button variant="contained" onClick={saveDraft}>{t("AdminPromptsPage.saveDraftButton")}</Button>
            <Typography variant="subtitle1">{t("AdminPromptsPage.versionsHeading")}</Typography>
            {versions.map((v) => (
              <Stack key={v.id} direction="row" spacing={2} alignItems="center">
                <Chip label={`v${v.version}`} size="small" color={v.status === "active" ? "success" : "default"} />
                <Typography variant="body2">{v.status}</Typography>
                {v.status !== "active" && (
                  <Button size="small" onClick={() => openDeploy(v.id)}>{t("AdminPromptsPage.deployButton")}</Button>
                )}
              </Stack>
            ))}
          </Stack>
        ) : (
          <Typography color="text.secondary">{t("AdminPromptsPage.selectPromptPrompt")}</Typography>
        )}
      </Box>

      <Dialog open={activateId !== null} onClose={() => setActivateId(null)}>
        <DialogTitle>{t("AdminPromptsPage.deployDialogTitle")}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1, minWidth: 360 }}>
            <Typography variant="body2">{t("AdminPromptsPage.deployImmediateNotice")}</Typography>
            {isCoach && (
              <Alert severity="warning">
                {t("AdminPromptsPage.coachEvalRequiredWarning")}
              </Alert>
            )}
            <TextField
              label={isCoach ? t("AdminPromptsPage.evalRunIdLabelRequired") : t("AdminPromptsPage.evalRunIdLabelOptional")}
              placeholder={t("AdminPromptsPage.evalRunIdPlaceholder")}
              value={evalRunId}
              onChange={(e) => setEvalRunId(e.target.value)}
              fullWidth
              size="small"
            />
            <FormControlLabel
              control={<Checkbox checked={force} onChange={(e) => setForce(e.target.checked)} />}
              label={t("AdminPromptsPage.forceOverrideLabel")}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActivateId(null)}>{t("AdminPromptsPage.cancelButton")}</Button>
          <Button variant="contained" onClick={confirmDeploy} disabled={activateDisabled}>
            {t("AdminPromptsPage.deployButton")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
